<template>
  <modal
    name="bank-account-transfer-form"
    :width="780"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="!saving"
    @closed="resetTransferForm"
  >
    <form class="modal-form bank-transfer-modal" @submit.prevent="saveTransfer">
      <div class="modal-form-header">
        <div class="d-flex align-items-center min-width-0">
          <span class="modal-title-icon" aria-hidden="true">
            <i class="fas fa-exchange-alt"></i>
          </span>

          <div class="min-width-0">
            <h4 class="modal-title mb-0">Nuevo traspaso</h4>
            <small class="modal-subtitle text-truncate d-block">
              Registra el cargo y el abono entre cuentas de la misma empresa.
            </small>
          </div>
        </div>

        <button
          type="button"
          class="close ml-3"
          :disabled="saving"
          title="Cerrar"
          aria-label="Cerrar formulario de traspaso"
          @click="closeTransferModal"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-form-body">
        <div v-if="formError" class="alert alert-danger" role="alert">
          <i class="fas fa-exclamation-circle mr-1" aria-hidden="true"></i>
          {{ formError }}
        </div>

        <section class="form-section" aria-labelledby="transfer-form-data">
          <h5 id="transfer-form-data" class="form-section-title">
            Datos del movimiento
          </h5>

          <div class="row">
            <div class="col-12 col-md-8 mb-3">
              <label for="transfer-company" class="required-label">Empresa</label>
              <select
                id="transfer-company"
                v-model="transferForm.company_id"
                class="form-control form-control-sm"
                required
                :disabled="saving || loadingCompanies"
                @change="onFormCompanyChange"
              >
                <option value="" disabled>Selecciona una empresa</option>
                <option
                  v-for="company in companies"
                  :key="company.id"
                  :value="company.id"
                >
                  {{ company.name }}
                </option>
              </select>

              <small v-if="loadingCompanies" class="form-text text-muted">
                <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
                Cargando empresas...
              </small>
            </div>

            <div class="col-12 col-md-4 mb-3">
              <label for="transfer-date" class="required-label">Fecha</label>
              <input
                id="transfer-date"
                v-model="transferForm.transfer_date"
                type="date"
                class="form-control form-control-sm"
                :max="today"
                required
                :disabled="saving"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-6 mb-3">
              <label for="source-account" class="required-label">
                Cuenta origen
              </label>
              <select
                id="source-account"
                v-model="transferForm.source_account_id"
                class="form-control form-control-sm"
                required
                :disabled="saving || loadingAccounts || !transferForm.company_id"
                @change="onSourceAccountChange"
              >
                <option value="" disabled>Selecciona la cuenta origen</option>
                <option
                  v-for="account in sourceAccounts"
                  :key="accountRecordId(account)"
                  :value="accountRecordId(account)"
                >
                  {{ accountLabel(account) }}
                </option>
              </select>

              <small v-if="loadingAccounts" class="form-text text-muted">
                <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
                Cargando cuentas...
              </small>
              <small
                v-else-if="transferForm.company_id && !sourceAccounts.length"
                class="form-text text-muted"
              >
                La empresa no tiene cuentas disponibles.
              </small>
            </div>

            <div class="col-12 col-md-6 mb-3">
              <label for="destination-account" class="required-label">
                Cuenta destino
              </label>
              <select
                id="destination-account"
                v-model="transferForm.destination_account_id"
                class="form-control form-control-sm"
                required
                :disabled="saving || loadingAccounts || !transferForm.source_account_id"
                @change="onDestinationAccountChange"
              >
                <option value="" disabled>Selecciona la cuenta destino</option>
                <option
                  v-for="account in destinationAccounts"
                  :key="accountRecordId(account)"
                  :value="accountRecordId(account)"
                >
                  {{ accountLabel(account) }}
                </option>
              </select>

              <small
                v-if="transferForm.source_account_id && !destinationAccounts.length"
                class="form-text text-muted"
              >
                No hay otra cuenta compatible para recibir el traspaso.
              </small>
            </div>
          </div>

          <div
            v-if="sourceBalance.loading || sourceBalance.loaded"
            class="balance-comparison mb-3"
            aria-live="polite"
          >
            <div class="balance-block balance-main">
              <span>Saldo disponible</span>
              <strong v-if="sourceBalance.loading">
                <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
                Consultando
              </strong>
              <strong v-else>{{ formatMoney(sourceBalance.current_balance) }}</strong>
              <small>Cuenta origen</small>
            </div>

            <div v-if="sourceBalance.loaded" class="balance-block balance-projected">
              <span>Saldo después</span>
              <strong :class="{ 'text-danger': amountExceedsBalance }">
                {{ formatMoney(projectedSourceBalance) }}
              </strong>
              <small>Estimado con este traspaso</small>
            </div>

            <div v-if="sourceBalance.loaded" class="balance-block balance-secondary">
              <span>Saldo inicial</span>
              <strong>{{ formatMoney(sourceBalance.initial_balance) }}</strong>
              <small>Desde {{ formatDate(sourceBalance.initial_balance_date) }}</small>
            </div>
          </div>

          <div
            v-if="sourceBalance.loaded && !sourceBalance.initial_balance_date"
            class="alert alert-warning py-2"
            role="alert"
          >
            <i class="fas fa-exclamation-triangle mr-1" aria-hidden="true"></i>
            La cuenta origen todavía no tiene una fecha de saldo inicial configurada.
          </div>

          <div v-if="currencyMismatch" class="alert alert-warning py-2" role="alert">
            <i class="fas fa-coins mr-1" aria-hidden="true"></i>
            Las cuentas seleccionadas no manejan la misma moneda.
          </div>

          <div class="row">
            <div class="col-12 col-md-5 mb-3">
              <label for="transfer-amount" class="required-label">Monto</label>
              <div class="input-group input-group-sm">
                <div class="input-group-prepend">
                  <span class="input-group-text">$</span>
                </div>
                <input
                  id="transfer-amount"
                  v-model.trim="transferForm.amount"
                  type="number"
                  class="form-control text-right"
                  :class="{ 'is-invalid': amountExceedsBalance }"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  required
                  :disabled="saving || !sourceBalance.loaded"
                />
                <div v-if="amountExceedsBalance" class="invalid-feedback">
                  El monto supera el saldo disponible.
                </div>
              </div>
            </div>

            <div class="col-12 col-md-7 mb-3">
              <div class="d-flex align-items-end justify-content-between">
                <label for="transfer-comment">Comentario</label>
                <small class="character-counter">
                  {{ transferForm.comment.length }}/2000
                </small>
              </div>
              <textarea
                id="transfer-comment"
                v-model.trim="transferForm.comment"
                class="form-control form-control-sm"
                rows="2"
                maxlength="2000"
                placeholder="Motivo o referencia del traspaso"
                :disabled="saving"
              ></textarea>
            </div>
          </div>
        </section>

        <section
          v-if="selectedSourceAccount || selectedDestinationAccount || transferForm.amount"
          class="transfer-summary"
          aria-label="Resumen del traspaso"
        >
          <div class="summary-account">
            <small>Sale de</small>
            <strong :title="selectedSourceAccountLabel">
              {{ selectedSourceAccountLabel }}
            </strong>
          </div>

          <span class="transfer-arrow" aria-hidden="true">
            <i class="fas fa-arrow-right"></i>
          </span>

          <div class="summary-account">
            <small>Llega a</small>
            <strong :title="selectedDestinationAccountLabel">
              {{ selectedDestinationAccountLabel }}
            </strong>
            <span v-if="destinationBalance.loaded">
              Saldo actual: {{ formatMoney(destinationBalance.current_balance) }}
            </span>
          </div>

          <div class="summary-amount">
            <small>Monto</small>
            <strong>{{ formatMoney(transferForm.amount || 0) }}</strong>
          </div>
        </section>
      </div>

      <div class="modal-form-footer">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="saving"
          @click="closeTransferModal"
        >
          Cancelar
        </button>

        <button
          type="submit"
          class="btn btn-navy btn-sm"
          :disabled="!canSaveTransfer"
        >
          <i
            :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"
            class="mr-1"
            aria-hidden="true"
          ></i>
          {{ saving ? 'Guardando...' : 'Registrar traspaso' }}
        </button>
      </div>
    </form>
  </modal>
</template>

<script>
import { mapActions } from 'vuex';
import request from '@/services/request';
import Catalogue from '@/services/catalogue';

const EMPTY_BALANCE = () => ({
  loading: false,
  loaded: false,
  initial_balance: '0.00',
  current_balance: '0.00',
  initial_balance_date: null,
});

const CURRENT_DATE = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const EMPTY_TRANSFER_FORM = () => ({
  company_id: '',
  source_account_id: '',
  destination_account_id: '',
  transfer_date: CURRENT_DATE(),
  amount: '',
  comment: '',
});

export default {
  name: 'ModalBankAccountTransferForm',

  data() {
    return {
      catalogue: new Catalogue(),
      loadingCompanies: false,
      loadingAccounts: false,
      saving: false,
      formError: '',
      sourceBalance: EMPTY_BALANCE(),
      destinationBalance: EMPTY_BALANCE(),
      transferForm: EMPTY_TRANSFER_FORM(),
      balanceRequestSequence: {
        sourceBalance: 0,
        destinationBalance: 0,
      },
    };
  },

  computed: {
    today() {
      return CURRENT_DATE();
    },

    companies() {
      const items = this.catalogue.companies_all
        || this.catalogue.companies
        || [];

      return Array.isArray(items) ? items : [];
    },

    bankAccountCompanies() {
      const items = this.catalogue.bank_accounts_companys || [];

      return Array.isArray(items) ? items : [];
    },

    sourceAccounts() {
      return this.bankAccountCompanies;
    },

    selectedSourceAccount() {
      return this.findAccount(this.transferForm.source_account_id);
    },

    selectedDestinationAccount() {
      return this.findAccount(this.transferForm.destination_account_id);
    },

    destinationAccounts() {
      const sourceCurrencyId = this.accountCurrencyId(this.selectedSourceAccount);

      return this.bankAccountCompanies.filter((account) => {
        if (
          String(this.accountRecordId(account))
          === String(this.transferForm.source_account_id)
        ) {
          return false;
        }

        const destinationCurrencyId = this.accountCurrencyId(account);

        if (sourceCurrencyId && destinationCurrencyId) {
          return String(sourceCurrencyId) === String(destinationCurrencyId);
        }

        return true;
      });
    },

    selectedSourceAccountLabel() {
      return this.selectedSourceAccount
        ? this.accountLabel(this.selectedSourceAccount)
        : 'Sin seleccionar';
    },

    selectedDestinationAccountLabel() {
      return this.selectedDestinationAccount
        ? this.accountLabel(this.selectedDestinationAccount)
        : 'Sin seleccionar';
    },

    currencyMismatch() {
      if (!this.selectedSourceAccount || !this.selectedDestinationAccount) {
        return false;
      }

      const sourceCurrencyId = this.accountCurrencyId(this.selectedSourceAccount);
      const destinationCurrencyId = this.accountCurrencyId(
        this.selectedDestinationAccount
      );

      return sourceCurrencyId !== null
        && destinationCurrencyId !== null
        && String(sourceCurrencyId) !== String(destinationCurrencyId);
    },

    amountExceedsBalance() {
      if (!this.sourceBalance.loaded || !this.transferForm.amount) {
        return false;
      }

      return Number(this.transferForm.amount)
        > Number(this.sourceBalance.current_balance);
    },

    projectedSourceBalance() {
      const currentBalance = Number(this.sourceBalance.current_balance || 0);
      const transferAmount = Number(this.transferForm.amount || 0);

      return currentBalance - transferAmount;
    },

    canSaveTransfer() {
      return !this.saving
        && !this.loadingAccounts
        && this.sourceBalance.loaded
        && this.transferForm.company_id !== ''
        && this.transferForm.source_account_id !== ''
        && this.transferForm.destination_account_id !== ''
        && this.transferForm.transfer_date !== ''
        && Number(this.transferForm.amount) > 0
        && !this.amountExceedsBalance
        && !this.currencyMismatch
        && String(this.transferForm.source_account_id)
          !== String(this.transferForm.destination_account_id);
    },
  },

  mounted() {
    this.loadCompanies();
  },

  created() {
    Event.$on('event-show-addBankAccountTransfers', this.openTransferModal);
  },

  beforeDestroy() {
    Event.$off('event-show-addBankAccountTransfers', this.openTransferModal);
  },

  methods: {
    ...mapActions({
      successNotification: 'alert/success',
    }),

    async loadCompanies() {
      if (this.companies.length) {
        return;
      }

      this.loadingCompanies = true;

      try {
        await this.catalogue.get_companies_all();
      } catch (error) {
        this.formError = this.getErrorMessage(error);
      } finally {
        this.loadingCompanies = false;
      }
    },

    async openTransferModal(payload) {
      this.resetTransferForm();
      this.$modal.show('bank-account-transfer-form');

      if (!this.companies.length) {
        await this.loadCompanies();
      }

      const companyId = payload && payload.company_id
        ? payload.company_id
        : '';

      if (companyId) {
        this.transferForm.company_id = companyId;
        await this.loadAccountsForCompany(companyId);
      }
    },

    closeTransferModal() {
      if (!this.saving) {
        this.$modal.hide('bank-account-transfer-form');
      }
    },

    resetTransferForm() {
      this.formError = '';
      this.transferForm = EMPTY_TRANSFER_FORM();
      this.sourceBalance = EMPTY_BALANCE();
      this.destinationBalance = EMPTY_BALANCE();
      this.balanceRequestSequence.sourceBalance += 1;
      this.balanceRequestSequence.destinationBalance += 1;

      if (Array.isArray(this.catalogue.bank_accounts_companys)) {
        this.catalogue.bank_accounts_companys = [];
      }
    },

    async onFormCompanyChange() {
      this.transferForm.source_account_id = '';
      this.transferForm.destination_account_id = '';
      this.transferForm.amount = '';
      this.sourceBalance = EMPTY_BALANCE();
      this.destinationBalance = EMPTY_BALANCE();
      this.balanceRequestSequence.sourceBalance += 1;
      this.balanceRequestSequence.destinationBalance += 1;

      await this.loadAccountsForCompany(this.transferForm.company_id);
    },

    async loadAccountsForCompany(companyId) {
      if (!companyId) {
        return;
      }

      this.loadingAccounts = true;
      this.formError = '';

      try {
        await this.catalogue.get_bank_accounts_companys(companyId);
      } catch (error) {
        this.formError = this.getErrorMessage(error);
      } finally {
        this.loadingAccounts = false;
      }
    },

    async onSourceAccountChange() {
      if (
        String(this.transferForm.source_account_id)
        === String(this.transferForm.destination_account_id)
      ) {
        this.transferForm.destination_account_id = '';
      }

      this.transferForm.amount = '';
      this.destinationBalance = EMPTY_BALANCE();
      this.balanceRequestSequence.destinationBalance += 1;

      await this.loadAccountBalance(
        this.transferForm.source_account_id,
        'sourceBalance'
      );
    },

    async onDestinationAccountChange() {
      await this.loadAccountBalance(
        this.transferForm.destination_account_id,
        'destinationBalance'
      );
    },

    async loadAccountBalance(accountId, target) {
      this.balanceRequestSequence[target] += 1;
      const requestSequence = this.balanceRequestSequence[target];

      if (!accountId) {
        this[target] = EMPTY_BALANCE();
        return;
      }

      const loadingBalance = EMPTY_BALANCE();
      loadingBalance.loading = true;
      this[target] = loadingBalance;
      this.formError = '';

      try {
        const response = await request.get(
          `/bank-account-companies/${accountId}/balance`
        );
        const body = this.unwrapHttpResponse(response);
        const data = body && body.data ? body.data : body;

        if (requestSequence !== this.balanceRequestSequence[target]) {
          return;
        }

        this[target] = {
          loading: false,
          loaded: true,
          initial_balance: data.initial_balance || '0.00',
          current_balance: data.current_balance || '0.00',
          initial_balance_date: data.initial_balance_date || null,
        };
      } catch (error) {
        if (requestSequence === this.balanceRequestSequence[target]) {
          this[target] = EMPTY_BALANCE();
          this.formError = this.getErrorMessage(error);
        }
      }
    },

    async saveTransfer() {
      if (!this.canSaveTransfer) {
        return;
      }

      this.saving = true;
      this.formError = '';

      try {
        const response = await request.post('/bank-account-transfers', {
          source_account_id: this.transferForm.source_account_id,
          destination_account_id: this.transferForm.destination_account_id,
          transfer_date: this.transferForm.transfer_date,
          amount: this.transferForm.amount,
          comment: this.transferForm.comment || null,
        });
        const body = this.unwrapHttpResponse(response);
        const transfer = body && (body.data || body.transfer)
          ? (body.data || body.transfer)
          : body;
        const message = body && body.message
          ? body.message
          : 'El traspaso se registró correctamente.';

        this.successNotification(message);
        this.$emit('saved', transfer);
        Event.$emit('event-load-bank-account-transfers');
        this.$modal.hide('bank-account-transfer-form');
      } catch (error) {
        this.formError = this.getErrorMessage(error);
      } finally {
        this.saving = false;
      }
    },

    findAccount(accountId) {
      return this.bankAccountCompanies.find((account) => (
        String(this.accountRecordId(account)) === String(accountId)
      )) || null;
    },

    accountRecordId(account) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      return account.bank_account_company_id
        || (account.pivot && account.pivot.id)
        || account.id
        || null;
    },

    accountEntity(account) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      return account.bank_account
        || account.bankAccount
        || account.banks_accounts
        || account;
    },

    accountCurrencyId(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return null;
      }

      return entity.currency_id
        || (entity.currency && entity.currency.id)
        || account.currency_id
        || (account.currency && account.currency.id)
        || null;
    },

    accountCurrencyCode(account) {
      const entity = this.accountEntity(account);
      const currency = entity && entity.currency ? entity.currency : null;

      return (currency && (currency.code || currency.iso_code))
        || (entity && entity.currency_code)
        || account.currency_code
        || '';
    },

    accountBank(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return null;
      }

      return entity.bank || entity.banks || entity.bank_data || null;
    },

    accountName(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return 'Cuenta no disponible';
      }

      const bank = this.accountBank(account);
      const accountId = this.accountRecordId(account) || entity.id || null;

      return entity.name
        || entity.account_name
        || entity.alias
        || (bank && bank.name)
        || (accountId ? `Cuenta #${accountId}` : 'Cuenta no disponible');
    },

    accountSecondary(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return '';
      }

      const bank = this.accountBank(account);
      const bankName = bank && bank.name ? bank.name : '';
      const number = entity.account_number
        || entity.number
        || account.account_number
        || account.number
        || '';
      const currency = this.accountCurrencyCode(account);

      return [bankName, number, currency]
        .filter((value, index, values) => value && values.indexOf(value) === index)
        .join(' · ');
    },

    accountLabel(account) {
      const secondary = this.accountSecondary(account);

      return secondary
        ? `${this.accountName(account)} — ${secondary}`
        : this.accountName(account);
    },

    formatMoney(value) {
      const amount = Number(value || 0);
      const formatted = new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number.isFinite(amount) ? amount : 0);

      return `$ ${formatted}`;
    },

    formatDate(value) {
      if (!value) {
        return 'No configurada';
      }

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

      return Number.isNaN(date.getTime())
        ? 'No disponible'
        : date.toLocaleDateString('es-MX');
    },

    unwrapHttpResponse(response) {
      if (
        response
        && typeof response.status === 'number'
        && Object.prototype.hasOwnProperty.call(response, 'data')
      ) {
        return response.data;
      }

      return response;
    },

    getErrorMessage(error) {
      const response = error && error.response ? error.response.data : null;
      const body = response || (error && error.data) || null;

      if (body && body.errors && typeof body.errors === 'object') {
        const firstKey = Object.keys(body.errors)[0];
        const firstError = firstKey ? body.errors[firstKey] : null;

        if (Array.isArray(firstError) && firstError.length) {
          return firstError[0];
        }

        if (typeof firstError === 'string') {
          return firstError;
        }
      }

      if (body && body.message) {
        return body.message;
      }

      if (error && error.message) {
        return error.message;
      }

      return 'Ocurrió un error al registrar el traspaso. Intenta nuevamente.';
    },
  },
};
</script>

<style src="@/assets/css/bank-account-transfer-modals.css"></style>

<style scoped>
.form-section {
  border: 1px solid #e5e9ef;
  border-radius: 0.35rem;
  padding: 1rem;
}

.form-section-title {
  border-bottom: 1px solid #edf0f3;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-bottom: 1rem;
  padding-bottom: 0.6rem;
  text-transform: uppercase;
}

.balance-comparison {
  background-color: #f8f9fa;
  border: 1px solid #dce2e8;
  border-radius: 0.35rem;
  display: flex;
}

.balance-block {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding: 0.75rem 1rem;
}

.balance-block + .balance-block {
  border-left: 1px solid #dce2e8;
}

.balance-block span,
.balance-block small,
.transfer-summary small {
  color: #64748b;
  font-size: 0.68rem;
}

.balance-block strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.balance-main strong {
  color: #001f3f;
  font-size: 1.15rem;
}

.balance-projected strong {
  color: #1f7a34;
  font-size: 0.95rem;
}

.balance-projected strong.text-danger {
  color: #dc3545 !important;
}

.balance-secondary strong {
  color: #475569;
  font-size: 0.9rem;
}

.transfer-summary {
  align-items: center;
  background-color: #f8f9fa;
  border: 1px solid #dce2e8;
  border-left: 4px solid #001f3f;
  border-radius: 0.35rem;
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
}

.summary-account {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.summary-account strong,
.summary-account span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-account strong {
  color: #334155;
  font-size: 0.76rem;
}

.summary-account span {
  color: #6c757d;
  font-size: 0.68rem;
}

.transfer-arrow {
  color: #64748b;
  flex: 0 0 auto;
}

.summary-amount {
  border-left: 1px solid #dce2e8;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  padding-left: 0.75rem;
  text-align: right;
}

.summary-amount strong {
  color: #001f3f;
  font-size: 1rem;
  white-space: nowrap;
}

.character-counter {
  color: #6c757d;
  font-size: 0.68rem;
}

.btn-navy {
  background-color: #001f3f;
  border-color: #001f3f;
  color: #fff;
}

.btn-navy:hover,
.btn-navy:focus {
  background-color: #00152b;
  border-color: #00152b;
  color: #fff;
}

@media (max-width: 767.98px) {
  .form-section {
    padding: 0.85rem;
  }

  .balance-comparison,
  .transfer-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .balance-block + .balance-block,
  .summary-amount {
    border-left: 0;
    border-top: 1px solid #dce2e8;
    padding-left: 0;
    padding-top: 0.75rem;
    text-align: left;
  }

  .transfer-arrow {
    align-self: center;
    transform: rotate(90deg);
  }
}
</style>
